/**
 * Roblox Exploitation Campaign Configuration
 * All hardcoded values extracted from components
 *
 * ✅ Campaign ID comes from database (not hardcoded here!)
 * ✅ Configuration is keyed by campaign NAME/TYPE, not database ID
 * ✅ Easy to update without touching component code
 */

// Campaign configuration type
interface BaseCampaignConfig {
  name: string
  displayName: string
  description?: string
  steps: any[]
  [key: string]: any
}

/**
 * ✅ IMPORTANT: This config is identified by campaign NAME, not database ID
 * The database campaign.id is dynamic and assigned by the database
 * This config will be matched to database campaigns by campaign.name or campaign type
 */
export const ROBLOX_CAMPAIGN_CONFIG: Omit<BaseCampaignConfig, 'campaignId'> & {
  campaignType: 'roblox-exploitation'  // Static identifier for matching
  campaignNames: string[]               // Possible names in database
} = {
  // Static identifiers for matching with database
  campaignType: 'roblox-exploitation',
  campaignNames: [
    'Roblox Exploitation',
    'Roblox Exploitation Campaign',
    'roblox-exploitation',
    // Add other possible names from your database
  ],

  campaignName: 'Roblox Exploitation Campaign',

  // Eligibility Rules
  eligibilityRules: {
    // Florida is excluded
    allowedStates: {
      type: 'exclude',
      states: ['FL', 'FLORIDA'],
      errorMessage: 'DQ: Florida residents are not eligible for this campaign'
    },

    // Incident must be within 2 years
    customRules: [
      {
        field: 'incidentHappenedLessThan2Years',
        operator: 'equals',
        value: false,
        message: 'DQ: Incident happened more than 2 years ago - not eligible'
      }
    ]
  },

  // Field Options (removes hardcoded dropdowns)
  fieldOptions: {
    // Relationship to victim options
    relationToVictim: [
      { value: 'Myself', label: 'Myself' },
      { value: 'Loved one', label: 'Loved one' }
    ],

    // Minor status options
    isMinor: [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ],

    // Incident timeframe options
    incidentHappenedLessThan2Years: [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No', disqualifies: true }
    ],

    // Knows Roblox gamer tag options
    knowsRobloxGamerTag: [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ],

    // Verbal permission options (for minors)
    verbalPermission: [
      {
        value: 'Verbal Permission given',
        label: 'Verbal Permission given',
        description: 'Auto-selected for minors'
      },
      {
        value: 'PC to call back with verbal permission',
        label: 'PC to call back with verbal permission'
      },
      {
        value: 'No verbal permission',
        label: 'No verbal permission',
        disqualifies: true
      }
    ],

    // Relationship to minor options
    relationshipToMinor: [
      {
        value: 'Parent/Guardian',
        label: 'Parent/Guardian'
      },
      {
        value: 'Custody of the child',
        label: 'Custody of the child'
      },
      {
        value: 'Documented authority to speak on behalf of the Injured Party',
        label: 'Documented authority to speak on behalf of the Injured Party'
      },
      {
        value: 'None',
        label: 'None',
        disqualifies: true
      }
    ]
  },

  // Auto-Population Rules
  autoPopulation: [
    // When "Myself" is selected, auto-populate injured party with same info
    {
      trigger: {
        field: 'relationToVictim',
        value: 'Myself'
      },
      actions: {
        injuredPartyFirstName: 'intakeFirstName',
        injuredPartyLastName: 'intakeLastName',
        injuredPartyEmail: 'intakeEmail',
        isMinor: false,
        hasLegalAuthorization: true
      }
    },

    // When "Loved one" is selected, auto-populate caller fields
    {
      trigger: {
        field: 'relationToVictim',
        value: 'Loved one'
      },
      actions: {
        callerFirstName: 'intakeFirstName',
        callerLastName: 'intakeLastName',
        isMinor: true
      }
    },

    // When minor status is set to Yes, auto-set verbal permission
    {
      trigger: {
        field: 'isMinor',
        value: true
      },
      actions: {
        verbalPermission: 'Verbal Permission given',
        hasLegalAuthorization: true
      }
    },

    // When minor status is set to No with "Myself", set legal authorization
    {
      trigger: {
        field: 'isMinor',
        value: false
      },
      actions: {
        hasLegalAuthorization: true
      }
    },

    // Sync intakeDOB to injuredPartyDOB
    {
      trigger: {
        field: 'intakeDOB',
        value: null // Triggers on any value change
      },
      actions: {
        injuredPartyDOB: 'intakeDOB'
      }
    },

    // Sync intakeState to injuredPartyResidentState
    {
      trigger: {
        field: 'intakeState',
        value: null
      },
      actions: {
        injuredPartyResidentState: 'intakeState'
      }
    },

    // Sync robloxGamerTag to robloxUsername
    {
      trigger: {
        field: 'robloxGamerTag',
        value: null
      },
      actions: {
        robloxUsername: 'robloxGamerTag'
      }
    }
  ],

  // Field Dependencies (conditional required fields)
  fieldDependencies: [
    // If minor, require verbal permission and relationship to minor
    {
      condition: {
        field: 'isMinor',
        value: true
      },
      requiredFields: ['verbalPermission', 'relationshipToMinor']
    },

    // If "Loved one", require caller fields
    {
      condition: {
        field: 'relationToVictim',
        value: 'Loved one'
      },
      requiredFields: ['callerFirstName', 'callerLastName']
    },

    // If knows gamer tag is Yes, require gamer tag field
    {
      condition: {
        field: 'knowsRobloxGamerTag',
        value: true
      },
      requiredFields: ['robloxGamerTag']
    }
  ],

  // Business Rules
  businessRules: {
    minorsCannotSelfEnroll: true,
    requireVerbalPermissionForMinors: true,
    requireLegalGuardianForAdultLovedOnes: true,
    autoSetConsent: true
  }
}

/**
 * Helper to check if a database campaign matches this config
 */
export function isRobloxCampaign(campaign: { id: string; name: string }): boolean {
  const normalizedName = campaign.name.toLowerCase().trim()
  return ROBLOX_CAMPAIGN_CONFIG.campaignNames.some(
    name => name.toLowerCase() === normalizedName
  )
}

/**
 * Export constants for backward compatibility
 */
export const ROBLOX_STATES_EXCLUDED = ['FL', 'FLORIDA']
export const ROBLOX_MAX_INCIDENT_YEARS = 2

export const ROBLOX_MESSAGES = {
  FLORIDA_DQ: 'DQ: Florida residents are not eligible for this campaign',
  INCIDENT_TOO_OLD_DQ: 'DQ: Incident happened more than 2 years ago - not eligible',
  MINORS_CANNOT_SELF_ENROLL: 'Minors can not enroll themselves',
  VERBAL_PERMISSION_REQUIRED: 'Verbal permission is required for minors',
  NO_RELATIONSHIP_TO_MINOR_DQ: 'DQ: Must have legal relationship to minor',
  LEGAL_GUARDIAN_REQUIRED: 'You must confirm that you are the legal guardian of this adult to proceed.'
}
